import React, { useMemo, useState } from 'react';
import { formatDateTime } from '../../../utils/format';
import notify from '../../../utils/notify';
import { confirmAction } from '../../../utils/confirm';
import { FormSection } from '../../../Components/Form';
import CargoTable from '../components/CargoTable';
import TimerSetupModal from '../components/TimerSetupModal';
import NegotiationDoneBanner from '../components/NegotiationDoneBanner';
import '../../../Components/Common/Detail/detail.css';
import { CARGO_STATUS, PACKAGE_STATUS } from '../utils/negotiationStatus';

const NegotiationWorkspace = ({
  detail,
  mode = 'buyer',
  canStartTimer = true,
  nowMs,
  unit,
  partnerFilter = null,
  onPartnerFilterChange,
  onStartCargo,
  onExtendCargo,
  onOpenRoom,
}) => {
  const isSupplier = mode === 'supplier';
  // { cargo, isExtend } while the timer modal is open.
  const [timerTarget, setTimerTarget] = useState(null);
  const cargoes = useMemo(() => detail?.cargoes || [], [detail?.cargoes]);

  const summary = useMemo(() => {
    const count = status =>
      cargoes.filter(c => c.statusLabel === status).length;
    return {
      total: cargoes.length,
      notStarted: count(CARGO_STATUS.NOT_STARTED),
      active: count(CARGO_STATUS.UNDER_NEGOTIATION),
      accepted: count(CARGO_STATUS.PRICE_ACCEPTED),
    };
  }, [cargoes]);

  const handleConfirmTimer = async body => {
    const { cargo, isExtend } = timerTarget || {};
    if (!cargo) return;
    if (isExtend) {
      const ok = await confirmAction({
        tone: 'warning',
        title: 'Extend the negotiation deadline?',
        message:
          'Every partner of this cargo gets the new deadline; overdue lines become active again.',
        subject: `${cargo.cargoName} · ${formatDateTime(`${body.date}T${body.time}:00+07:00`)} WIB`,
        confirmLabel: 'Extend deadline',
      });
      if (!ok) return;
    }
    try {
      await (isExtend ? onExtendCargo : onStartCargo)(cargo.id, body);
      notify.success(
        isExtend
          ? 'Deadline extended successfully.'
          : 'Negotiation started successfully.'
      );
      setTimerTarget(null);
    } catch (error) {
      notify.apiError(
        error,
        isExtend ? 'extend the deadline' : 'start negotiation'
      );
    }
  };

  return (
    <div>
      {detail?.statusLabel === PACKAGE_STATUS.DONE && (
        <div className='mb-3'>
          <NegotiationDoneBanner mode={mode} />
        </div>
      )}

      <dl className='dt-facts dt-facts--card mb-3'>
        {[
          { icon: 'ri-ship-line', label: 'Cargoes', value: summary.total },
          {
            icon: 'ri-time-line',
            label: 'Not started',
            value: summary.notStarted,
          },
          {
            icon: 'ri-chat-3-line',
            label: 'In negotiation',
            value: summary.active,
          },
          {
            icon: 'ri-checkbox-circle-line',
            label: 'Price accepted',
            value: summary.accepted,
          },
        ].map(fact => (
          <div className='dt-fact' key={fact.label}>
            <span className='dt-fact__icon' aria-hidden='true'>
              <i className={fact.icon} />
            </span>
            <div>
              <dt className='dt-fact__label'>{fact.label}</dt>
              <dd className='dt-fact__value mb-0'>{fact.value}</dd>
            </div>
          </div>
        ))}
      </dl>

      <FormSection
        title='Cargo workspace'
        description={
          isSupplier
            ? 'Open a room when the buyer has started negotiation'
            : 'Start or extend each cargo timer, then open a room per partner'
        }
        icon='ri-ship-line'
        columns={1}
      >
        <div className='hy-field--full'>
          <CargoTable
            cargoes={cargoes}
            mode={mode}
            canStartTimer={canStartTimer}
            nowMs={nowMs}
            unit={unit}
            partnerFilter={partnerFilter}
            onPartnerFilterChange={onPartnerFilterChange}
            onStart={cargo => setTimerTarget({ cargo, isExtend: false })}
            onExtend={cargo => setTimerTarget({ cargo, isExtend: true })}
            onOpenRoom={onOpenRoom}
          />
        </div>
      </FormSection>

      <TimerSetupModal
        isOpen={Boolean(timerTarget)}
        toggle={() => setTimerTarget(null)}
        onConfirm={handleConfirmTimer}
        isExtend={Boolean(timerTarget?.isExtend)}
        minDeadline={timerTarget?.isExtend ? timerTarget.cargo.deadline : null}
        clockOffsetMs={detail?.clockOffsetMs || 0}
      />
    </div>
  );
};

export default NegotiationWorkspace;
